import React, { useState, useEffect } from 'react';
import { Input, Select, FormGroup } from '../components/Form';
import { Plus, X, Edit2, Trash2, ArrowUpCircle } from 'lucide-react';
import { getTodasPessoas as getMembros, salvarPessoa, promoverPessoa } from '../services/api';
import { getCargos } from '../services/cargos';
import { criarUsuario, listarUsuariosGhl } from '../services/usuarios';
import { supabase } from '../lib/supabase';
import { logError } from '../lib/logError';
import './Pessoas.css';

const INITIAL_FORM = {
    nomeOriginal: '',
    nome: '',
    cargo: '',
    cargo_id: '',
    email: '',
    atividade: 'ativo',
    ghl_user_id: '',
    // Só na criação: provisionam login e vínculo com o GHL de uma vez.
    senha: '',
    ghlModo: 'vincular',
    ghlUserId: '',
};

function Pessoas({ embedded = false }) {
    const [membros, setMembros] = useState([]);
    const [mostrarInativos, setMostrarInativos] = useState(false);
    const [cargos, setCargos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });
    const [formData, setFormData] = useState(INITIAL_FORM);
    // Promoção
    const [promovendo, setPromovendo] = useState(null); // { id, nome, cargo_id_atual }
    const [promocaoForm, setPromocaoForm] = useState({ cargo_id: '', motivo: '' });
    const [isPromovendo, setIsPromovendo] = useState(false);
    // Usuários do GHL disponíveis para vínculo (evita copiar id na mão, que já deixou gente sem vínculo)
    const [usuariosGhl, setUsuariosGhl] = useState([]);
    const [erroGhl, setErroGhl] = useState('');

    const carregarMembros = async () => {
        setIsLoading(true);
        setStatusMessage({ type: '', text: '' });
        try {
            const [data, listaCargos] = await Promise.all([getMembros(), getCargos({ apenasAtivos: true })]);
            setMembros(data);
            setCargos(listaCargos);
        } catch {
            setStatusMessage({ type: 'error', text: 'Erro ao carregar dados. Atualize a página.' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { carregarMembros(); }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const openModal = (mode, membro = null) => {
        setModalMode(mode);
        setStatusMessage({ type: '', text: '' });
        setErroGhl('');
        setFormData(mode === 'edit' && membro ? {
            ...INITIAL_FORM,
            nomeOriginal: membro.nome,
            nome: membro.nome,
            cargo: membro.cargo,
            cargo_id: membro.cargo_id || '',
            email: membro.email || '',
            atividade: membro.atividade || 'ativo',
            ghl_user_id: membro.ghl_user_id || '',
        } : INITIAL_FORM);
        setIsModalOpen(true);

        // A lista do GHL só serve para criar; na edição o campo de id continua manual.
        if (mode === 'add' && usuariosGhl.length === 0) {
            listarUsuariosGhl()
                .then(setUsuariosGhl)
                .catch(() => setErroGhl('Não consegui listar os usuários do GHL. Você ainda pode criar sem vínculo e ligar depois.'));
        }
    };

    const openPromover = (membro) => {
        setPromovendo({ id: membro.id, nome: membro.nome, cargo_id_atual: membro.cargo_id });
        setPromocaoForm({ cargo_id: '', motivo: '' });
    };

    const handlePromover = async () => {
        if (!promocaoForm.cargo_id) return;
        setIsPromovendo(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            await promoverPessoa({
                pessoaId: promovendo.id,
                novoCargoId: promocaoForm.cargo_id,
                motivo: promocaoForm.motivo || null,
                autorEmail: user?.email || null,
            });
            setStatusMessage({ type: 'success', text: `${promovendo.nome} promovido(a) com sucesso!` });
            setPromovendo(null);
            carregarMembros();
        } catch (err) {
            setStatusMessage({ type: 'error', text: err?.message || 'Erro ao promover.' });
        } finally {
            setIsPromovendo(false);
        }
    };

    const closeModal = () => { setIsModalOpen(false); setFormData(INITIAL_FORM); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setStatusMessage({ type: '', text: '' });
        const cargoSelecionado = cargos.find(c => c.id === formData.cargo_id);

        try {
            // Criar passa pelo provisionamento completo (login + pessoa + GHL + alias numa
            // operação só). Editar continua no caminho antigo: não mexe em login nem no GHL.
            if (modalMode === 'add') {
                const r = await criarUsuario({
                    nome: formData.nome,
                    email: formData.email,
                    senha: formData.senha,
                    cargo: cargoSelecionado?.nome || formData.cargo,
                    cargo_id: formData.cargo_id || null,
                    ghlModo: formData.ghlModo,
                    ghlUserId: formData.ghlUserId,
                });
                const avisos = r?.avisos ?? [];
                setStatusMessage(avisos.length
                    ? { type: 'warn', text: `Usuário criado, com pendências: ${avisos.join(' | ')}` }
                    : { type: 'success', text: `${formData.nome} criado com login, cadastro e vínculo do GHL.` });
                carregarMembros();
                // Com aviso a mensagem fica na tela até o gestor fechar — é o que ele precisa resolver.
                if (!avisos.length) setTimeout(() => closeModal(), 1800);
                return;
            }

            const response = await salvarPessoa({
                ...formData,
                cargo: cargoSelecionado?.nome || formData.cargo,
                cargo_id: formData.cargo_id || null,
                acao: 'editar',
            });
            if (response?.success) {
                setStatusMessage({ type: 'success', text: 'Pessoa atualizada com sucesso!' });
                carregarMembros();
                setTimeout(() => closeModal(), 1500);
            } else {
                setStatusMessage({ type: 'error', text: 'Erro: ' + (response?.error || 'Desconhecido') });
            }
        } catch (err) {
            setStatusMessage({ type: 'error', text: err?.message || 'Erro de comunicação ao salvar os dados.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (membro) => {
        if (!window.confirm(`Desativar ${membro.nome}? A pessoa sai das listas ativas, mas o histórico é preservado. Você pode reativá-la depois pela edição.`)) return;
        setIsLoading(true);
        try {
            const response = await salvarPessoa({ acao: 'excluir', nomeOriginal: membro.nome });
            if (response?.success) {
                setStatusMessage({ type: 'success', text: 'Pessoa desativada com sucesso!' });
                carregarMembros();
            } else {
                setStatusMessage({ type: 'error', text: 'Erro ao desativar: ' + (response?.error || 'Desconhecido') });
            }
        } catch (e) {
            logError(e, { fn: 'handleDelete', nome: membro?.nome });
            setStatusMessage({ type: 'error', text: 'Erro ao desativar pessoa: ' + (e?.message || 'desconhecido') });
        } finally {
            setIsLoading(false);
        }
    };

    const membrosVisiveis = mostrarInativos ? membros : membros.filter(m => m.atividade !== 'inativo');
    const inativosCount = membros.filter(m => m.atividade === 'inativo').length;

    return (
        <>
            {!embedded && (
                <div className="page-header">
                    <div className="page-header-left">
                        <div>
                            <div className="page-kicker">Configurações</div>
                            <h1 className="page-title">Time Interno</h1>
                        </div>
                    </div>
                    <div className="page-header-right">
                        <button className="pill pill-primary pill-sm" onClick={() => openModal('add')}>
                            <Plus size={14} style={{ marginRight: '0.3rem' }} />
                            Nova Pessoa
                        </button>
                    </div>
                </div>
            )}

            <div className="page-body" style={embedded ? { minHeight: 'auto' } : undefined}>
                {embedded && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                        <button className="pill pill-primary pill-sm" onClick={() => openModal('add')}>
                            <Plus size={14} style={{ marginRight: '0.3rem' }} />
                            Nova Pessoa
                        </button>
                    </div>
                )}
                {statusMessage.text && !isModalOpen && (
                    <div className={`alert alert-${statusMessage.type === 'success' ? 'success' : 'error'}`}>
                        {statusMessage.text}
                    </div>
                )}

                {isLoading ? (
                    <div className="loading-state">Carregando time...</div>
                ) : membros.length === 0 ? (
                    <div className="empty-state">Nenhuma pessoa encontrada.</div>
                ) : (
                    <div className="card">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                            <p className="section-label" style={{ margin: 0 }}>Membros ({membrosVisiveis.length})</p>
                            {inativosCount > 0 && (
                                <button
                                    type="button"
                                    className="pill pill-ghost pill-sm"
                                    onClick={() => setMostrarInativos(v => !v)}
                                >
                                    {mostrarInativos ? 'Ocultar inativos' : `Mostrar inativos (${inativosCount})`}
                                </button>
                            )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {membrosVisiveis.map((membro, i) => (
                                <div
                                    key={i}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '0.875rem 0',
                                        borderBottom: i < membrosVisiveis.length - 1 ? '1px solid var(--glass-border)' : 'none',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                                        <div style={{
                                            width: '34px', height: '34px', borderRadius: '50%',
                                            background: 'var(--canvas-2)',
                                            border: '1px solid var(--glass-border)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '0.8125rem', fontWeight: 700, color: 'var(--emerald)',
                                            flexShrink: 0,
                                        }}>
                                            {membro.nome?.charAt(0)?.toUpperCase()}
                                        </div>
                                        <div>
                                            <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9375rem', color: 'var(--ink)' }}>{membro.nome}</p>
                                            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--muted)' }}>
                                                {(() => {
                                                    const c = cargos.find(x => x.id === membro.cargo_id);
                                                    return c ? `${c.nome} — Nível ${c.nivel}` : membro.cargo;
                                                })()}
                                            </p>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                        <span className={membro.atividade === 'ativo' ? 'chip chip-active' : 'chip chip-muted'} style={{ fontSize: '0.7rem' }}>
                                            {membro.atividade === 'ativo' ? 'Ativo' : 'Inativo'}
                                        </span>
                                        <button
                                            className="pill pill-ghost pill-sm"
                                            onClick={() => openPromover(membro)}
                                            title="Promover / trocar cargo"
                                            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                                        >
                                            <ArrowUpCircle size={13} /> Promover
                                        </button>
                                        <button
                                            className="pill pill-ghost pill-sm"
                                            onClick={() => openModal('edit', membro)}
                                            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                                        >
                                            <Edit2 size={13} /> Editar
                                        </button>
                                        <button
                                            className="pill pill-ghost pill-sm"
                                            onClick={() => handleDelete(membro)}
                                            style={{ color: 'var(--danger-soft)', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {promovendo && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Promover — {promovendo.nome}</h2>
                            <button className="modal-close" onClick={() => setPromovendo(null)}><X size={22} /></button>
                        </div>
                        <div className="modal-body">
                            <FormGroup label="Novo Cargo" htmlFor="prom_cargo_id">
                                <Select
                                    id="prom_cargo_id"
                                    value={promocaoForm.cargo_id}
                                    onChange={e => setPromocaoForm(p => ({ ...p, cargo_id: e.target.value }))}
                                >
                                    <option value="">Selecione o novo cargo</option>
                                    {cargos.filter(c => c.id !== promovendo.cargo_id_atual).map(c => (
                                        <option key={c.id} value={c.id}>{c.nome} — Nível {c.nivel}</option>
                                    ))}
                                </Select>
                            </FormGroup>
                            <FormGroup label="Motivo (opcional)" htmlFor="prom_motivo">
                                <Input
                                    id="prom_motivo"
                                    value={promocaoForm.motivo}
                                    onChange={e => setPromocaoForm(p => ({ ...p, motivo: e.target.value }))}
                                    placeholder="Ex: Bateu meta por 3 meses seguidos"
                                />
                            </FormGroup>
                            <div className="modal-footer">
                                <button type="button" className="pill pill-ghost" onClick={() => setPromovendo(null)}>Cancelar</button>
                                <button
                                    type="button"
                                    className="pill pill-primary"
                                    disabled={isPromovendo || !promocaoForm.cargo_id}
                                    onClick={handlePromover}
                                >
                                    {isPromovendo ? 'Promovendo...' : 'Confirmar Promoção'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>{modalMode === 'add' ? 'Nova Pessoa' : 'Editar Pessoa'}</h2>
                            <button className="modal-close" onClick={closeModal}><X size={22} /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="modal-body">
                            {statusMessage.text && (
                                <div
                                    className={`alert alert-${statusMessage.type === 'success' ? 'success' : 'error'}`}
                                    style={{ marginBottom: '1rem' }}
                                    role="status"
                                >
                                    {statusMessage.text}
                                </div>
                            )}

                            <FormGroup label="Nome Completo" htmlFor="nome">
                                <Input id="nome" name="nome" value={formData.nome} onChange={handleChange} required placeholder="Ex: João Silva" />
                            </FormGroup>
                            <FormGroup label="Cargo" htmlFor="cargo_id">
                                <Select id="cargo_id" name="cargo_id" value={formData.cargo_id} onChange={handleChange} required>
                                    <option value="">Selecione o cargo</option>
                                    {cargos.map(c => (
                                        <option key={c.id} value={c.id}>{c.nome} — Nível {c.nivel}</option>
                                    ))}
                                </Select>
                            </FormGroup>
                            {formData.cargo_id && (() => {
                                const c = cargos.find(x => x.id === formData.cargo_id);
                                if (!c) return null;
                                const fmt = (tipo, val) => tipo === 'fixed' ? `R$ ${parseFloat(val).toFixed(2)}` : `${(parseFloat(val) * 100).toFixed(2)}%`;
                                return (
                                    <p style={{ fontSize: '0.775rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
                                        Taxas: Agend. {fmt(c.comissao_agendamento_tipo, c.comissao_agendamento_valor)} · Venda {fmt(c.comissao_venda_tipo, c.comissao_venda_valor)} · Indicação {fmt(c.comissao_indicacao_tipo, c.comissao_indicacao_valor)}
                                    </p>
                                );
                            })()}
                            <FormGroup label="E-mail (login no desk)" htmlFor="email">
                                <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="Ex: joao@orbion.com" />
                            </FormGroup>

                            {modalMode === 'add' ? (
                                <>
                                    <FormGroup label="Senha de acesso" htmlFor="senha">
                                        <Input
                                            id="senha" name="senha" type="text" value={formData.senha}
                                            onChange={handleChange} required minLength={8}
                                            placeholder="Mínimo 8 caracteres"
                                        />
                                        <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
                                            Combine com a pessoa para ela trocar no primeiro acesso.
                                        </p>
                                    </FormGroup>

                                    <FormGroup label="Usuário no GHL" htmlFor="ghlModo">
                                        <Select id="ghlModo" name="ghlModo" value={formData.ghlModo} onChange={handleChange}>
                                            <option value="vincular">Vincular a um usuário que já existe no GHL</option>
                                            <option value="criar">Criar um usuário novo no GHL</option>
                                            <option value="nenhum">Sem vínculo por enquanto</option>
                                        </Select>
                                    </FormGroup>

                                    {formData.ghlModo === 'vincular' && (
                                        <FormGroup label="Qual usuário do GHL" htmlFor="ghlUserId">
                                            <Select id="ghlUserId" name="ghlUserId" value={formData.ghlUserId} onChange={handleChange} required>
                                                <option value="">Selecione…</option>
                                                {usuariosGhl.map(u => (
                                                    <option key={u.id} value={u.id} disabled={!!u.usadoPor}>
                                                        {u.nome} {u.email ? `(${u.email})` : ''}{u.usadoPor ? ` — já é ${u.usadoPor}` : ''}
                                                    </option>
                                                ))}
                                            </Select>
                                            {erroGhl && (
                                                <p style={{ fontSize: '0.75rem', color: 'var(--warn, #b26b00)', marginTop: '0.25rem' }}>{erroGhl}</p>
                                            )}
                                        </FormGroup>
                                    )}

                                    <FormGroup label="Atividade" htmlFor="atividade">
                                        <Select id="atividade" name="atividade" value={formData.atividade} onChange={handleChange}>
                                            <option value="ativo">Ativo</option>
                                            <option value="inativo">Inativo</option>
                                        </Select>
                                    </FormGroup>
                                </>
                            ) : (
                                <div className="grid-2">
                                    <FormGroup label="Atividade" htmlFor="atividade">
                                        <Select id="atividade" name="atividade" value={formData.atividade} onChange={handleChange}>
                                            <option value="ativo">Ativo</option>
                                            <option value="inativo">Inativo</option>
                                        </Select>
                                    </FormGroup>
                                    <FormGroup label="ID no GHL" htmlFor="ghl_user_id">
                                        <Input id="ghl_user_id" name="ghl_user_id" value={formData.ghl_user_id} onChange={handleChange} placeholder="Ex: abc123xyz" />
                                    </FormGroup>
                                </div>
                            )}

                            <div className="modal-footer">
                                <button type="button" className="pill pill-ghost" onClick={closeModal}>Cancelar</button>
                                <button type="submit" className="pill pill-primary" disabled={isSubmitting}>
                                    {isSubmitting
                                        ? (modalMode === 'add' ? 'Criando acesso...' : 'Salvando...')
                                        : (modalMode === 'add' ? 'Criar usuário completo' : 'Atualizar Dados')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}

export default Pessoas;
